import { DEVICE_LOG_TYPE, ILogOptions } from 'dto/device-dto';
import { DeploymentGroupType } from 'dto/deployment/deployment-group-dto';
import { action, makeObservable, observable } from 'mobx';
import { DeviceDetailEntryStore, IDeviceDetailsConstructorOptions } from './device-detail-entry-store';
import { formatDate } from 'common-utils/date-utils';
import { getErrorMessage } from 'common-utils';
import moment from 'moment';

interface IDeviceLogCodeProps {
    logName: string;
    code: string;
}

const DEFAULT_DEVICE_LOGS: IDeviceLogCodeProps[] = [
    { code: DEVICE_LOG_TYPE.SYSLOGS, logName: 'System Logs' },
    { code: DEVICE_LOG_TYPE.TERMLOG, logName: 'Term Logs' },
];

const RAPTOR_DEVICE_LOGS: IDeviceLogCodeProps[] = [
    { code: DEVICE_LOG_TYPE.GGL_SYSTEM_LOGS, logName: 'System Logs' },
];

export interface ISystemLogs extends IDeviceLogCodeProps {
    key?: number;
    date?: string;
    logEntries: any[];
}

export interface IDeviceLogs {
    code: string;
    logEntries: ILogOptions[];
}

export interface IDisplayDeviceLog {
    key: number;
    logName: string;
    code: string;
    displayLogEntriesData: ILogOptions[];
}

interface IDeviceLogsConstructorOptions extends Omit<IDeviceDetailsConstructorOptions, 'code'> {
    platform: string
}
export class DeviceLogsStore extends DeviceDetailEntryStore<IDeviceLogs> {
    private readonly platform: string;

    constructor(options: IDeviceLogsConstructorOptions) {
        super('device logs', options);
        makeObservable(this);
        this.platform = options.platform;

        this.setDate(moment());
    }

    /** ************************************
     * Override functions
     **************************************/
    @observable
    public dateFrom: moment.Moment;

    @observable
    public dateTo: moment.Moment;

    @action
    public setDate(date: moment.Moment): void {
        this.dateFrom = date.clone().startOf('day');
        this.dateTo = date.clone().endOf('day');
    }

    @action
    public setDateFrom(dateFrom: moment.Moment): void {
        this.dateFrom = dateFrom.clone();
    }

    @action
    public setDateTo(dateTo: moment.Moment): void {
        this.dateTo = dateTo.clone();
    }

    public get isRaptor(): boolean {
        return this.platform === DeploymentGroupType.RAPTOR;
    }

    private get deviceLogs(): IDeviceLogCodeProps[] {
        return this.isRaptor ? RAPTOR_DEVICE_LOGS : DEFAULT_DEVICE_LOGS;
    }

    public get defaultLogType(): string {
        return this.deviceLogs[0].code;
    }

    protected async initializeData(): Promise<IDeviceLogs[]> {
        return Promise.all(
            this.deviceLogs.map(async (item) => {
                try {
                    const logEntries = await this._devicesStore.getDeviceLogs(this.accountId, this.uid, item.code, {
                        from: this.dateFrom.toLocaleString(),
                        to: this.dateTo.toLocaleString(),
                        limit: 10000
                    });
                    return { code: item.code, logEntries };
                } catch (err) {
                    if (getErrorMessage(err).includes('does not exist')) return { code: item.code, logEntries: [] };
                    throw err;
                }
            }),
        );

    }
    /** *************************************/
    public displayDeviceLogs(logType: string): ILogOptions[] {
        if (!this.entities || this.entities.length == 0) return [];
        const today = new Date();
        const currentYear = today.getFullYear();
        const timestampRegex = /\w{3}\s*\d{1,2}\s*\d{2}:\d{2}:\d{2}/;
        let logEntries: ILogOptions[] = [];
        this.entities.filter(item => item.code === logType).forEach((log) => {
            logEntries = log.logEntries;
        });
        return logEntries.map((item, index) => {
            const timestampMatch = item.data.match(timestampRegex);
            return {
                key: `logrecord-${index}`,
                timestamp: formatDate(timestampMatch ? currentYear + timestampMatch : item.timestamp),
                data: item.data,
            };
        });

    }
}
